import React, { useCallback, useEffect, useRef, useState } from "react";
import { Empty, Spin } from "antd";
import { useNavigate, useParams } from "react-router-dom";
import { Wrapper } from "./styles";
import { getAPICustom } from "../../utils/api";
import { APP_NAME, VALIDATE_TOKEN_API_URL } from "../../utils/constants";
import { StyledTitle } from "../../components/CustomAntdComponents/Typography";

type TokenPayload = {
  MerchantCode: string;
  MerchantHash: string;
};

function Index(): React.ReactElement {
  const navigate = useNavigate();
  const { MerchantCode: paramCode, MerchantHash: paramHash } = useParams();
  const urlSearchParams = new URLSearchParams(window.location.search);
  const customerName = urlSearchParams.get("CustomerName");
  const customerId = urlSearchParams.get("CustomerId"); 
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  // Helper function to sanitize and validate credentials
  const sanitizeCredentials = useCallback((code: string | null, hash: string | null) => {
    if (!code || !hash) return null;

    // Basic sanitization - remove any potential XSS characters
    const sanitizedCode = code.replace(/[<>\"'&]/g, "").trim();
    const sanitizedHash = hash.replace(/[<>\"'&]/g, "").trim();

    // Basic validation - ensure they're not empty after sanitization
    if (!sanitizedCode || !sanitizedHash) return null;

    return { MerchantCode: sanitizedCode, MerchantHash: sanitizedHash };
  }, []);

  const validateUser = useCallback(async ({ MerchantCode, MerchantHash }: TokenPayload) => {
    const networErrorMsg = "Network error. Please check your connection and try again.";
    setLoading(true);
    setError("");
    try {
      const validateApi = await getAPICustom(
        VALIDATE_TOKEN_API_URL,
        {},
        {
          MerchantCode,
          MerchantHash,
          AppName: APP_NAME,
        }
      );

      setLoading(false);

      if (validateApi.status === 200) {
        localStorage.setItem("trips_auth", JSON.stringify(validateApi.data));
        if (customerName || customerId) {
          localStorage.setItem(
              "trips_user",
              JSON.stringify({
                CustomerName: customerName || "",
                CustomerId: customerId || "",
              })
            );
         }
        window.location.href = "/home";
        return;
      }
      switch (validateApi.status) {
        case 401:
        case 400:
        case 403:
          // Unauthorized - Invalid merchant details (Merchant Integration Issue)
          setError(validateApi.data || "Authentication failed. Please check your credentials and try again.");
          break;

        default:
          // Other HTTP errors

          setError(validateApi.data || networErrorMsg);
      }
    } catch (err) {
      setLoading(false);
      console.error("Network/API error:", err);
      if (
        err.name === "NetworkError" ||
        err.message?.includes("fetch") ||
        err.message?.includes("network") ||
        err.message?.includes("Failed to fetch") ||
        err.code === "NETWORK_ERROR"
      ) {
        setError(networErrorMsg);
        return;
      }

      // Check for timeout errors
      if (err.name === "TimeoutError" || err.message?.includes("timeout") || err.code === "TIMEOUT") {
        setError("Connection timed out. Please check your internet connection and try again.");
        return;
      }
      // Generic fallback for unknown errors
      setError(networErrorMsg);
    }
  }, []);

  useEffect(() => {
    try {
      // Method 1: URL path parameters (v3 style) - /MerchantCode/MerchantHash
      if (paramCode && paramHash) {
        const sanitized = sanitizeCredentials(paramCode, paramHash);
        if (sanitized) {
          validateUser(sanitized);
          return;
        }
      }

      // Method 2: URL query parameters (v2 style) - ?MerchantCode=X&MerchantHash=Y
      const urlSearchParams = new URLSearchParams(window.location.search);
      const queryCode = urlSearchParams.get("MerchantCode");
      const queryHash = urlSearchParams.get("MerchantHash");

      if (queryCode && queryHash) {
        const sanitized = sanitizeCredentials(queryCode, queryHash);
        if (sanitized) {
          validateUser(sanitized);
          return;
        }
      }

      // Method 3: localStorage fallback (WebView injection)
      const localCode = localStorage.getItem("dxz");
      const localHash = localStorage.getItem("xdg");

      if (localCode && localHash) {
        const sanitized = sanitizeCredentials(localCode, localHash);
        if (sanitized) {

          validateUser(sanitized);

          // Handle additional user data from localStorage
          const customerName = localStorage.getItem("CustomerName");
          const customerId = localStorage.getItem("CustomerId");

          if (customerName || customerId) {
            localStorage.setItem(
              "trips_user",
              JSON.stringify({
                CustomerName: customerName || "",
                CustomerId: customerId || "",
              })
            );
          }
          return;
        }
      }

      // Method 4: Check if already authenticated
      const existingAuth = localStorage.getItem("trips_auth");
      if (existingAuth) {
        try {
          // Validate the existing auth is not corrupted
          JSON.parse(existingAuth);
          navigate("/home");
          return;
        } catch (parseError) {
          // Clear corrupted auth data
          localStorage.removeItem("trips_auth");
        }
      }

      // If none of the above methods work
      setError("You need authorization to access this app. Please ensure you have valid credentials.");
    } catch (err) {
      console.error("Error processing authentication:", err);
      setError("Failed to load widget. Please refresh or contact support.");
    }
  }, [paramCode, paramHash, validateUser, navigate, sanitizeCredentials]);

  // Clear error when component unmounts or when loading starts
  useEffect(() => {
    if (loading) {
      setError("");
    }
  }, [loading]);

  return (
    <Wrapper>
      <Spin spinning={loading} tip="Loading content..." />
      {error && !loading && (
        <Empty
          description={
            <div>
              <StyledTitle level={4}>{error}</StyledTitle>
              {error.includes("authorization") && (
                <p style={{ marginTop: 8, fontSize: "12px", color: "#666" }}>
                  Please contact your system administrator for access credentials.
                </p>
              )}
            </div>
          }
        />
      )}
    </Wrapper>
  );
}

export default Index;
